// components/ui/EmptyState.tsx
import { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { Card } from './Card';
import { Button } from './Button';
import { clsx } from 'clsx';
import { Sparkles } from 'lucide-react';

interface EmptyStateProps {
    icon: ReactNode;
    title: string;
    description?: string;
    action?: {
        label: string;
        onClick: () => void;
    };
    secondaryAction?: {
        label: string;
        onClick: () => void;
    };
    className?: string;
    size?: 'sm' | 'md' | 'lg';
}

export function EmptyState({
    icon,
    title,
    description,
    action,
    secondaryAction,
    className,
    size = 'md'
}: EmptyStateProps) {
    const sizes: Record<string, { icon: string; title: string; padding: string }> = {
        sm: { icon: 'w-16 h-16 rounded-2xl', title: 'text-lg', padding: 'py-12' },
        md: { icon: 'w-24 h-24 rounded-3xl', title: 'text-xl', padding: 'py-16' },
        lg: { icon: 'w-32 h-32 rounded-[2rem]', title: 'text-2xl', padding: 'py-24' },
    };

    const s = sizes[size];

    return (
        <Card className={clsx('border-2 border-dashed border-slate-200 dark:border-slate-800', className)}>
            <div className={clsx('flex flex-col items-center justify-center text-center px-6', s.padding)}>
                {/* Animated Icon Container */}
                <motion.div
                    initial={{ scale: 0, rotate: -20 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{
                        type: 'spring',
                        stiffness: 200,
                        damping: 15,
                        delay: 0.1,
                    }}
                    className={clsx(
                        'bg-gradient-to-br from-slate-100 to-slate-50 dark:from-slate-800 dark:to-slate-900 flex items-center justify-center mb-6 shadow-inner relative overflow-hidden',
                        s.icon
                    )}
                >
                    {/* Decorative sparkles */}
                    <motion.div
                        animate={{
                            opacity: [0, 1, 0],
                            scale: [0.8, 1.2, 0.8],
                        }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="absolute -top-1 -right-1"
                    >
                        <Sparkles className="w-4 h-4 text-amber-400" />
                    </motion.div>
                    <motion.div
                        animate={{
                            opacity: [0, 1, 0],
                            scale: [1, 0.8, 1],
                        }}
                        transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                        className="absolute -bottom-1 -left-1"
                    >
                        <Sparkles className="w-3 h-3 text-amber-300" />
                    </motion.div>

                    <motion.div
                        animate={{
                            y: [0, -8, 0],
                        }}
                        transition={{
                            duration: 3,
                            repeat: Infinity,
                            ease: 'easeInOut',
                        }}
                        className="text-slate-300 dark:text-slate-600"
                    >
                        {icon}
                    </motion.div>
                </motion.div>

                {/* Title */}
                <motion.h3
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.25, duration: 0.4 }}
                    className={clsx('font-black text-slate-800 dark:text-slate-100', s.title)}
                >
                    {title}
                </motion.h3>

                {/* Description */}
                {description && (
                    <motion.p
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.35, duration: 0.4 }}
                        className="text-sm text-slate-500 dark:text-slate-400 mt-3 max-w-md leading-relaxed"
                    >
                        {description}
                    </motion.p>
                )}

                {/* Actions */}
                {(action || secondaryAction) && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.45, duration: 0.4 }}
                        className="flex items-center gap-3 mt-8"
                    >
                        {action && (
                            <Button size="lg" onClick={action.onClick} glow>
                                {action.label}
                            </Button>
                        )}
                        {secondaryAction && (
                            <Button variant="secondary" size="lg" onClick={secondaryAction.onClick}>
                                {secondaryAction.label}
                            </Button>
                        )}
                    </motion.div>
                )}
            </div>
        </Card>
    );
}